import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  addSaga,
  createAsyncSaga,
  createSagaReducer,
  persist,
  sagaSlice,
} from "./sagaStore";
import FetchCustomerRelationsService from "../services/fetchCustomerRelations";
import FetchCustomerSummaryService from "../services/fetchCustomerSummary";
import FetchCustomerFrequentlyOrderedItemsService from "../services/fetchCustomerFrequentlyOrderedItems";
import {
  CustomerBadge,
  CustomerOrderSummaryData,
  CustomerSummaryData,
  FrequentlyOrderedItemsData,
} from "../types/customers";

export interface CustomersState {
  list: {
    loading: boolean;
    error: string | null;
    data: CustomerOrderSummaryData | Record<string, unknown>;
  };
  detail: {
    loading: boolean;
    error: string | null;
    data: CustomerSummaryData | Record<string, unknown>;
  };
  frequentlyOrdered: {
    loading: boolean;
    error: string | null;
    data: FrequentlyOrderedItemsData | Record<string, unknown>;
  };
  // List page filters
  searchTerm: string;
  badge: CustomerBadge | null;
  isPurchased: boolean | null;
  startDate: string;
  endDate: string;
  currentPage: number;
  // Detail page keeps its OWN date range so a change on the detail page
  // doesn't leak back into the list page's filter. On navigation from
  // list → detail, we seed this from `startDate` / `endDate`.
  detailStartDate: string;
  detailEndDate: string;
}

const initialState: CustomersState = {
  list: {
    loading: false,
    error: null,
    data: {},
  },
  detail: {
    loading: false,
    error: null,
    data: {},
  },
  frequentlyOrdered: {
    loading: false,
    error: null,
    data: {},
  },
  searchTerm: "",
  badge: null,
  isPurchased: null,
  startDate: dayjs().startOf("day").toISOString(),
  endDate: dayjs().endOf("day").toISOString(),
  currentPage: 1,
  detailStartDate: dayjs().startOf("day").toISOString(),
  detailEndDate: dayjs().endOf("day").toISOString(),
};

const Slice: sagaSlice = createSlice({
  ...persist("customers", initialState),
  reducers: {
    ...createSagaReducer("fetchCustomerRelations", "list"),
    ...createSagaReducer("fetchCustomerSummary", "detail"),
    ...createSagaReducer(
      "fetchCustomerFrequentlyOrderedItems",
      "frequentlyOrdered"
    ),
    updateSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    updateBadge(state, action) {
      state.badge = action.payload;
    },
    updateIsPurchased(state, action) {
      state.isPurchased = action.payload;
    },
    updateDateRange(state, action) {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    updateDetailDateRange(state, action) {
      state.detailStartDate = action.payload.startDate;
      state.detailEndDate = action.payload.endDate;
    },
    clearCustomerDetail(state) {
      state.detail = { loading: false, error: null, data: {} };
      state.frequentlyOrdered = { loading: false, error: null, data: {} };
    },
  },
});

const fetchCustomerRelationsSaga = createAsyncSaga(
  "fetchCustomerRelations",
  Slice,
  FetchCustomerRelationsService.fetchCustomerRelations,
  [
    "shopId",
    "page",
    "limit",
    "isPurchased",
    "startDate",
    "endDate",
    "searchTerm",
    "badge",
  ]
);

const fetchCustomerSummarySaga = createAsyncSaga(
  "fetchCustomerSummary",
  Slice,
  FetchCustomerSummaryService.fetchCustomerSummary,
  ["shopId", "phoneNumber", "userFirebaseId", "startDate", "endDate"]
);

const fetchCustomerFrequentlyOrderedItemsSaga = createAsyncSaga(
  "fetchCustomerFrequentlyOrderedItems",
  Slice,
  FetchCustomerFrequentlyOrderedItemsService.fetchCustomerFrequentlyOrderedItems,
  ["shopId", "phoneNumber"]
);

addSaga(Slice.actions.fetchCustomerRelations.type, fetchCustomerRelationsSaga);
addSaga(Slice.actions.fetchCustomerSummary.type, fetchCustomerSummarySaga);
addSaga(
  Slice.actions.fetchCustomerFrequentlyOrderedItems.type,
  fetchCustomerFrequentlyOrderedItemsSaga
);

export const {
  fetchCustomerRelations,
  fetchCustomerSummary,
  fetchCustomerFrequentlyOrderedItems,
  updateSearchTerm,
  updateBadge,
  updateIsPurchased,
  updateDateRange,
  setCurrentPage,
  updateDetailDateRange,
  clearCustomerDetail,
} = Slice.actions;

export default Slice.reducer;
